/**
 * HistoryVersionList
 *
 * Renders the grouped, chronological list of content history snapshots.
 * Each entry is clickable to open the diff viewer.
 */
import History from '@mui/icons-material/History';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

export interface HistoryEntry {
  id: string;
  seq: number;
  dateTime: string;
  isCurrent?: boolean;
}

export interface HistoryGroup {
  date: string;
  items: HistoryEntry[];
}

interface HistoryVersionListProps {
  groups: HistoryGroup[];
  contentId: string;
  onSelectVersion: (contentId: string, dayKey: string, seq: number, id: string) => void;
}

export function HistoryVersionList({
  groups,
  contentId,
  onSelectVersion,
}: HistoryVersionListProps) {
  const { t } = useTranslation();

  if (groups.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', opacity: 0.5 }}>
        <History sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="body2">{t('editor.no_history_yet')}</Typography>
      </Box>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <Box key={group.date}>
          {/* Day header */}
          <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'primary.main', letterSpacing: 1.5, fontSize: '0.65rem' }}
            >
              {group.date}
            </Typography>
          </Box>

          <List dense disablePadding>
            {group.items.map((item) => {
              const timestamp = typeof item.dateTime === 'string'
                ? new Date(item.dateTime)
                : new Date();
              const timeStr = timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

              return (
                <ListItemButton
                  key={item.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    bgcolor: item.isCurrent ? 'rgba(var(--mui-palette-primary-mainChannel), 0.08)' : 'transparent',
                    position: 'relative',
                    '&:hover': {
                      bgcolor: item.isCurrent ? 'rgba(var(--mui-palette-primary-mainChannel), 0.15)' : 'action.hover',
                    },
                  }}
                  onClick={() => onSelectVersion(contentId, group.date, item.seq, item.id)}
                >
                  {/* Active variant indicator bar */}
                  {item.isCurrent && (
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: 'primary.main' }} />
                  )}
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: item.isCurrent ? 700 : 500,
                            color: item.isCurrent ? 'primary.main' : 'text.primary',
                          }}
                        >
                          {item.isCurrent ? t('editor.current_version') : timeStr}
                        </Typography>
                        <Chip
                          label={`v${Number.isNaN(Number(item.seq)) ? '?' : (item.seq ?? 0)}`}
                          size="small"
                          color={item.isCurrent ? 'primary' : 'default'}
                          variant={item.isCurrent ? 'filled' : 'outlined'}
                          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                        />
                      </Box>
                    }
                    secondary={item.isCurrent
                      ? t('editor.live_prose')
                      : `ID: ${item.id.substring(0, 8)}...`}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      sx: { opacity: 0.7, color: item.isCurrent ? 'primary.main' : 'text.secondary' },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      ))}
    </>
  );
}
